import type { ReactNode } from 'react';
import cn from 'clsx';
import { useAiSearch } from '@pkgs/components/hooks/queries/useAiSearch';
import { Copy } from '@ev-tech/ui-core-components/Copy';
import { Switch as BaseSwitchPrimitive } from '@base-ui/react/switch';

import styles from './Switch.module.css';

export type BaseSwitchProps = {
  label: string;
  className?: string;
  'data-testid'?: string;
  isSelected?: boolean;
  defaultSelected?: boolean;
  onChange?: (isSelected: boolean) => void;
  isDisabled?: boolean;
  name?: string;
  id?: string;
  children?: ReactNode;
};

export const BaseSwitch = ({
  className,
  label,
  isSelected,
  defaultSelected,
  onChange,
  isDisabled,
  name,
  id,
  'data-testid': dataTestId,
}: BaseSwitchProps) => (
  <label className={cn(styles.switch, className)}>
    <Copy size="l" text={label} />
    <BaseSwitchPrimitive.Root
      id={id}
      name={name}
      checked={isSelected}
      defaultChecked={defaultSelected}
      disabled={isDisabled}
      onCheckedChange={(checked) => onChange?.(checked)}
      className={styles.track}
      data-testid={dataTestId}
    >
      <BaseSwitchPrimitive.Thumb className={styles.thumb} />
    </BaseSwitchPrimitive.Root>
  </label>
);

export type SwitchProps = BaseSwitchProps & {
  hasAiStyle?: boolean;
};

export const Switch = ({ className, hasAiStyle, label, isDisabled, ...switchProps }: SwitchProps) => {
  const { isPending: isAiSearchFetching } = useAiSearch();
  const disabled = isAiSearchFetching || isDisabled;

  if (hasAiStyle) {
    return (
      <div className={styles.aiSwitchContainer}>
        <label className={cn(styles.aiSwitch, className)}>
          <Copy
            className={styles.aiCopy}
            size="l"
            text={
              <span className={styles.aiCopyLabel} data-testid="search-components_ai-switch-label">
                {label}
              </span>
            }
          />
          <BaseSwitchPrimitive.Root
            checked={switchProps.isSelected}
            defaultChecked={switchProps.defaultSelected}
            disabled={disabled}
            onCheckedChange={(checked) => switchProps.onChange?.(checked)}
            name={switchProps.name}
            id={switchProps.id}
            className={styles.aiTrack}
            data-testid={switchProps['data-testid']}
          >
            <BaseSwitchPrimitive.Thumb className={styles.aiThumb} />
          </BaseSwitchPrimitive.Root>
        </label>
      </div>
    );
  }

  return <BaseSwitch {...switchProps} className={className} isDisabled={disabled} label={label} />;
};
